// Mock data for General Harmonics MSML website

export const heroStats = [
  { value: '', label: 'Higher information density with preserved fidelity: pack more signal, drop the waste.', icon: 'database' },
  { value: '', label: 'Lower latency, faster reactions: important pieces arrive first.', icon: 'zap' },
  { value: '', label: 'Energy efficiency: Reduces modern LLMs cost of energy per output.', icon: 'battery' },
  { value: '', label: 'Bandwidth reduction and Throughput increase: Aimed at brain-like massive parallelism "pre-training" by evolution and life-long learning.', icon: 'wifi' },
  { value: '', label: 'Adaptation: Continually adapt online; LLMs are fixed post-training. They cannot truly plan or remember beyond their training.', icon: 'target' },
  { value: '', label: 'Interpretability and Safety: Makes LLMs decision process more transparent, verifiable and accountable.', icon: 'clock' }
];

export const navigationItems = [
  { name: 'Technology', href: '#technology' },
  { name: 'Products', href: '#products' },
  { name: 'Markets', href: '#markets' },
  { name: 'Team', href: '#team' },
  { name: 'Investors', href: '#investment' }
];

export const aiLLMFeatures = [
  {
    title: 'MSML-LLMC Framework',
    description: 'Revolutionary mathematical framework combining hierarchical multi-scale decomposition with perceptive information value criteria.',
    metrics: ['300x compression ratios', '6x improvement vs single-method', 'Energy efficiency gains']
  },
  {
    title: 'LLM Data Optimization',
    description: 'Reduces size and optimizes performance of Large Language Models while preserving semantic integrity.',
    metrics: ['Lower computational demands', 'Reduced carbon footprints', 'Enhanced ML efficiency']
  },
  {
    title: 'AI Workflow Enhancement',
    description: 'Provides theoretical foundation for enhancing AI workflows across speech recognition, NLP, and machine learning.',
    metrics: ['Improved accuracy', 'Faster training cycles', 'Cross-modal applications']
  }
];

export const mediaPartners = [
  { 
    name: 'D1 AUDIO', 
    description: 'Programmable Media Container with fidelity lossless codec for 3D Hi-Res audio and advanced metadata',
    url: 'https://www.pulsbeat.ca/',
    features: ['STEMS for creators', 'Live Lyrics & Chords', 'Data Steganography']
  },
  { 
    name: 'AudioMine', 
    description: 'Cloud infrastructure platform with Attention Reward RPM Tokens and Intelligent Live Link Assistant',
    url: 'https://audiomineapp.com/',
    features: ['Content Module', 'AudioDNA Module', 'ArtAssets Module']
  },
  { 
    name: 'cDM Cloud', 
    description: 'Cognitive Dynamic Media Cloud with AI-driven media solutions and global distribution networks',
    url: 'https://cdmcloud.ai/',
    features: ['Server Infrastructure', 'Audio Optimization', 'Secure Transmission']
  },
  { 
    name: 'my.ARTIST.one', 
    description: 'Artist Branded App Ecosystem with D9NAMIC media player and LIVELINK synchronization',
    url: 'https://myartist.one/',
    features: ['Personalized apps', 'Fan engagement', 'Real-time sync']
  }
];

export const compressionDemo = {
  before: {
    fileSize: '122 MB',
    bandwidth: '1,411 kbps',
    format: 'Original .wav',
    quality: 'Uncompressed'
  },
  after: {
    fileSize: '5.8 MB',
    bandwidth: '70 kbps',
    format: 'D1 Audio .zsa',
    quality: 'Perceptually Lossless'
  }
};

export const d1AudioSamples = {
  original: {
    title: 'SAMPLE 1 ORIGINAL',
    bitrate: '1,411 kbps',
    format: '.wav',
    size: '122 MB',
    description: '44.1-48kHz/16bit stereo uncompressed audio',
    spectrogram: 'https://customer-assets.emergentagent.com/job_edge-intelligence-1/artifacts/mb2l9nwa_Sample%201%20Spectrogram%20Original%2048kHz-16bit.png'
  },
  compressed: {
    title: 'SAMPLE 1 D1 AUDIO',
    bitrate: '70 kbps',
    format: '.zsa',
    size: '5.8 MB',
    description: 'IDDO compressed with perceptual transparency',
    spectrogram: 'https://customer-assets.emergentagent.com/job_edge-intelligence-1/artifacts/deaelcuw_Sample%201%20Spectrogram%20D1%20AUDIO%20Processed%20%28zsa%20decoded%29.png'
  },
  compressionRatio: '21x reduced data volume and optimized for'
};

export const telecomBenefits = [
  {
    icon: 'globe',
    title: '5G Network Optimization',
    description: 'Reduce 5G data transmission costs by up to 75% while maintaining service quality.'
  },
  {
    icon: 'satellite',
    title: 'Satellite Communications',
    description: 'Enable efficient data transmission in remote areas with limited bandwidth.'
  },
  {
    icon: 'server',
    title: 'Edge Computing',
    description: 'Process data locally with compressed AI models for ultra-low latency applications.'
  }
];

export const roboticsApplications = [
  {
    platform: 'NVIDIA Jetson',
    capabilities: ['Real-time object detection', 'Autonomous navigation', 'Voice processing'],
    performance: '300x compression, <10ms inference'
  },
  {
    platform: 'Intel NUC',
    capabilities: ['Multi-model deployment', 'Edge AI processing', 'Sensor fusion'],
    performance: 'Up to 8 models simultaneously'
  },
  {
    platform: 'ARM Cortex',
    capabilities: ['Ultra-low power AI', 'Battery optimization', 'Real-time decision making'],
    performance: '90% power reduction'
  }
];

export const msmlFeatures = [
  {
    title: 'Multi-Structure',
    description: 'Handles diverse data types including neural networks, media files, and structured data.',
    benefit: 'Universal compatibility'
  },
  {
    title: 'Multi-Layer',
    description: 'Progressive compression with multiple optimization layers for maximum efficiency.',
    benefit: 'Adaptive compression'
  },
  {
    title: 'Data Formalization',
    description: 'Standardizes data representation across different domains and applications.',
    benefit: 'Seamless integration'
  }
];

export const contactForms = {
  demo: {
    title: 'Request Demo',
    description: 'See MSML in action with your specific use case',
    fields: [
      { name: 'requestTopic', label: 'Request Topic', type: 'select', options: ['Product Demo', 'Technical Consultation', 'Brand & IP Collateral', 'Partnership Inquiry', 'General Inquiry'], required: true },
      { name: 'name', label: 'Full Name', type: 'text', required: true },
      { name: 'email', label: 'Email Address', type: 'email', required: true },
      { name: 'company', label: 'Company', type: 'text', required: true },
      { name: 'useCase', label: 'Use Case', type: 'select', options: ['AI & LLMs', 'Media Processing', 'Telecom & Edge', 'AV and Robotics & Autonomous Systems'], required: true },
      { name: 'message', label: 'Project Details', type: 'textarea', required: false }
    ]
  },
  license: {
    title: 'Inquire to License',
    description: 'Get licensing information for commercial deployment',
    fields: [
      { name: 'requestTopic', label: 'Request Topic', type: 'select', options: ['Licensing Inquiry', 'Commercial Deployment', 'Brand & IP Collateral', 'Technical Documentation', 'Enterprise Solutions'], required: true },
      { name: 'name', label: 'Full Name', type: 'text', required: true },
      { name: 'email', label: 'Business Email', type: 'email', required: true },
      { name: 'company', label: 'Company Name', type: 'text', required: true },
      { name: 'title', label: 'Job Title', type: 'text', required: true },
      { name: 'industry', label: 'Industry', type: 'select', options: ['Technology', 'Media & Entertainment', 'Telecommunications', 'Automotive', 'Healthcare', 'Other'], required: true },
      { name: 'deploymentScale', label: 'Expected Deployment Scale', type: 'select', options: ['Proof of Concept', 'Small Scale (< 1K users)', 'Medium Scale (1K-100K users)', 'Large Scale (100K+ users)', 'Enterprise'], required: true },
      { name: 'message', label: 'Licensing Requirements', type: 'textarea', required: false }
    ]
  }
};

// Image URLs from vision expert
export const images = {
  hero: 'https://images.unsplash.com/photo-1485740112426-0c2549fa8c86?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwyfHxmdXR1cmlzdGljJTIwdGVjaG5vbG9neXxlbnwwfHx8fDE3NjEwODA0NDB8MA&ixlib=rb-4.1.0&q=85',
  aiBackground: 'https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwyfHxuZXVyYWwlMjBuZXR3b3Jrc3xlbnwwfHx8fDE3NjEwODA0NDV8MA&ixlib=rb-4.1.0&q=85',
  neuralNetwork: 'https://images.unsplash.com/photo-1674027444485-cec3da58eef4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHw0fHxuZXVyYWwlMjBuZXR3b3Jrc3xlbnwwfHx8fDE3NjEwODA0NDV8MA&ixlib=rb-4.1.0&q=85',
  dataCompression: 'https://images.unsplash.com/photo-1756756736901-a2bf24f2d2de?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxkYXRhJTIwY29tcHJlc3Npb258ZW58MHx8fHwxNzYxMDgwNDUwfDA&ixlib=rb-4.1.0&q=85',
  technology: 'https://images.unsplash.com/photo-1627645835237-0743e52b991f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwzfHxmdXR1cmlzdGljJTIwdGVjaG5vbG9neXxlbnwwfHx8fDE3NjEwODA0NDB8MA&ixlib=rb-4.1.0&q=85',
  abstract: 'https://images.unsplash.com/photo-1660165458059-57cfb6cc87e5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHw0fHxmdXR1cmlzdGljJTIwdGVjaG5vbG9neXxlbnwwfHx8fDE3NjEwODA0NDB8MA&ixlib=rb-4.1.0&q=85',
  network: 'https://images.pexels.com/photos/17484901/pexels-photo-17484901.png',
  compression: 'https://images.unsplash.com/photo-1705167066594-6fbbb333cdf0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHw0fHxkYXRhJTIwY29tcHJlc3Npb258ZW58MHx8fHwxNzYxMDgwNDUwfDA&ixlib=rb-4.1.0&q=85'
};